<script lang="ts" setup>
import { computed, reactive, ref, PropType, watch } from 'vue';
import { useRouter } from 'vue-router';
import { openRouterLink } from '@/utils/router-utils';
import { deepParseDates } from '@/utils/date-utils';
import {
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { highlightJson } from '@/utils/prism-utils';
import ComputeTaskDebugDetails from '@/components/compute/ComputeTaskDebugDetails.vue';
import TaskResultState from '@/components/compute/TaskResultState.vue';
import { useProjectTaskQuery } from '@/composables/project-task-polling';
import { BucketType, ComputeTaskType, DataObjectType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue-next';
import { COMPUTE_TASK_TYPES, PAGING_ITEMS_PER_PAGE } from '@/constants';
import { TaskFileType } from '@/components/compute/ComputeTaskFilesList.vue';
import NextflowTask from '@/components/compute/NextflowTask.vue';
import ComputeTaskAbort from '@/components/compute/ComputeTaskAbort.vue';
import { useUserStore } from '@/store/user';
import { getFileDownloadUrl } from '@/utils/fileService';
import DataObjectDetail from '@/components/data/DataObjectDetail.vue';

const router = useRouter();
const userStore = useUserStore();

// --- Component Properties ---
const props = defineProps({
  projectId: { type: Number as PropType<number | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  // additinal component props
  messageNoItemsFound: {
    type: String,
    default: 'No compute tasks found.',
  },
  taskNameColumnTitle: {
    type: String,
    default: 'Task Name',
  },
  showExecDetails: {
    type: Boolean,
    default: true,
  },
});

// --- Component State ---
const disableTransitionConfig = ref<'none' | 'multi' | 'all'>('multi'); // none, multi (multiple cards only), all (disable single card animations too)
const disableTransitionsState = ref(disableTransitionConfig.value === 'all');
const selectedTask = ref<ComputeTaskType | null>(null);
const selectedDataObject = ref<DataObjectType | null>(null);
const selectedObjectDownloadAllowed = computed(
  () => selectedDataObject.value?.isFolder === false
);
const showModalDataObjectDetail = ref(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return tasksQueryResult.value?.computeTasks?.total || 0;
});

// Tab selection for tasks & subtasks
// get helper with safe default
const selectedTabByTaskId = reactive<
  Record<number, DataPipelineTaskTabOptionType>
>({});

function getSelectedTab(id?: number | null) {
  if (!id) {
    return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
  }
  const selectedTab = selectedTabByTaskId[id];
  if (selectedTab) {
    return selectedTab;
  }
  // otherwise check whether the task is root or subtask and set default tab accordingly
  const task = tasks.value.find((t) => t.id === id);
  if (task && task.parentId) {
    // subtask - default to progress
    return DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS;
  }

  // otherwise consider root task - default to files
  return DATA_PIPELINE_TASK_TAB_OPTIONS.FILES;
}

function setSelectedTab(
  id?: number | null,
  val: DataPipelineTaskTabOptionType = DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
) {
  if (id) {
    selectedTabByTaskId[id] = val;
  }
}

// Expanded/Collapsed tasks & subtasks
// get helper with safe default
const expandedByTaskId = reactive<Record<number, boolean>>({});

function getExpanded(id?: number | null) {
  if (!id) {
    return false;
  }
  // if id not in the map, default to false unless root task with no parent and multiple subtasks
  if (expandedByTaskId[id] === undefined) {
    const task = rootTasks.value.find(
      (t) => t.id === id && t.taskType === COMPUTE_TASK_TYPES.COMPUTE_PIPELINE
    );
    if (task) {
      // root compute pipeline task - default to expanded view if files are available
      return !!(task.results.filesAvailable || task.results.files?.length);
    } else {
      // otherwise default to collapsed
      return false;
    }
  }
  // otherwise return the value
  return expandedByTaskId[id];
}

// write helper used by child event
function setExpanded(id?: number | null, val = false) {
  if (id) {
    expandedByTaskId[id] = val;
    return setTaskExpanded(id, val);
  }
}

// --- Compute Tasks List ---
const tasks = computed<ComputeTaskType[]>(() => {
  return (
    tasksQueryResult.value?.computeTasks?.computeTasks
      ?.filter((task) => task && task !== null)
      .map((task) => ({
        ...(deepParseDates(task) as ComputeTaskType),
      })) || []
  );
});

// --- Compute Task Hierarchy ---
const taskIdList = computed(() => {
  const ids: number[] = [];
  tasks.value.forEach((task) => {
    if (task && task.id) {
      ids.push(task.id);
    }
  });
  return ids;
});

const rootTasks = computed(() => {
  // find all root tasks (parentId is null)
  const roots = tasks.value.filter((task) => !task.parentId);
  // also add elements with parentId that are not in the list of root tasks
  tasks.value.forEach((task) => {
    if (task && task.parentId && !taskIdList.value.includes(task.parentId)) {
      roots.push(task);
    }
  });
  // ensure unique roots by id
  return roots;
});

// --- Component state ---

const noTasks = computed(() => {
  return tasks.value.length === 0;
});

// --- Query Compute Tasks ---
const taskFilters = computed(() => ({
  projectId: props.projectId,
  taskTypes: [COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE],
  pagingItemsPerPage: props.pagingItemsPerPage,
}));

watch(
  taskFilters,
  () => {
    pagingPageCurrent.value = 1;
  },
  { flush: 'sync' }
);

const taskQueryVariables = computed(() => ({
  ...taskFilters.value,
  pagingPageCurrent: pagingPageCurrent.value,
}));

const {
  result: tasksQueryResult,
  loading: tasksQueryInProgress,
  error: tasksQueryError,
  refetch: refetchTasks,
  onResult: onTasksQueryDone,
  onError: onTasksQueryError,
  detailErrors,
  setVisibleTasks,
  setExpanded: setTaskExpanded,
} = useProjectTaskQuery(taskQueryVariables);

watch(rootTasks, (rows) => setVisibleTasks(rows.map((task) => task.id!)), {
  immediate: true,
});

onTasksQueryDone((res) => {
  // Polling can remove the last page. Only a completed response may correct
  // it: Apollo can temporarily expose old or empty data while loading.
  if (!res.loading && typeof res.data?.computeTasks?.total === 'number') {
    const lastPage =
      props.pagingItemsPerPage > 0
        ? Math.max(
            1,
            Math.ceil(res.data.computeTasks.total / props.pagingItemsPerPage)
          )
        : 1;
    if (pagingPageCurrent.value > lastPage) {
      pagingPageCurrent.value = lastPage;
    }
  }

  if (res.data) {
    if (res.data.computeTasks) {
      console.debug('Tasks retrieved successfuly (ComputeTasksNested)');
    } else {
      console.error('Error, could not retrieve tasks:', res.data);
    }
  } else if (!res.loading) {
    console.error('Error, could not retrieve tasks result data:', res);
  }
});

onTasksQueryError((err) => {
  console.error('Error fetching code (onCodeError):', err.message);
});

function requestRefetchTasks() {
  refetchTasks()
    ?.catch((err) => {
      console.error('Refetch tasks failed:', err);
    })
    .then(() => {
      console.debug('Refetched tasks');
    });
}

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchTasks();
  }
}

// --- File and Directory Requests ---
function directoryInfoRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('directory-info-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: true,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

function directoryViewRequested(task: ComputeTaskType, file: TaskFileType) {
  if (!file.path) return;
  // The bucket browser lists virtual prefixes; a folder need not have a marker
  // object that the file-download API can sign.
  const path = file.path.endsWith('/') ? file.path : `${file.path}/`;
  bucketRequested(task.bucket, undefined, undefined, path);
}

function filePreviewRequested(task: ComputeTaskType, file: TaskFileType) {
  console.debug('file-preview-request', file);
  selectedTask.value = task;
  selectedDataObject.value = {
    isFolder: false,
    name: file.path,
  } as DataObjectType;
  showModalDataObjectDetail.value = true;
}

async function fileDownloadRequested(
  task: ComputeTaskType,
  file: TaskFileType
) {
  console.debug('file-download-request', file);
  // validate the selected data object
  if (!file.name) {
    console.error('No name found in selected data object');
    return;
  }
  if (!task?.bucket?.id) {
    console.error('Task Bucket ID not found');
    return;
  }
  // get the download URL
  const url = await getFileDownloadUrl(task.bucket.id, file.path);
  // validate the URL
  if (!url) {
    console.error(
      `Unexpected error fetching download URL for the file ${file.path}`
    );
    alert(
      'Failed to generate download link. The requested file might not be available anymore.'
    );
    return;
  }
  // open the URL in a new tab to trigger the download
  window.open(url, '_blank');
}

// --- Compute Task Abort Request ---
const showConfirmAbortModal = ref<boolean>(false);

function taskAbortRequested(task: ComputeTaskType) {
  if (!task?.id) {
    console.error('Task cancellation requested, but task ID is missing');
    return;
  }
  console.debug('Cancel task ', task.id, 'requested, waiting for confirmation');
  selectedTask.value = task;
  showConfirmAbortModal.value = true;
}

function closeTaskAbortModal() {
  showConfirmAbortModal.value = false;
}

// --- Debug Details ---
const debugTaskId = ref<number | null>(null);
const detailsContent = ref<any>(null);
const detailsModalTitle = ref('Compute Task Details');
const showDetailsModal = ref(false);

async function showExecDetails(task: ComputeTaskType) {
  if (task.id) {
    const loaded = await setExpanded(task.id, true);
    if (!loaded) return;
    task = loaded;
  }
  detailsModalTitle.value = task.name || 'Compute Task Exec Details';
  let censoredAttributes = task.attributes;

  // only allow certain attributes to be copied and viewed if not admin/developer
  if (!userStore.admin || !userStore.developer) {
    censoredAttributes = Object.fromEntries(
      Object.entries(task.attributes).filter(([key]) =>
        [
          // common attribues
          'samples', // bcl to fastq conversion + data pipeline
          'version',
          // ftp transfer
          'ftpCmd',
          'autoAnnotate',
          'reconstructData',
          'createProject',
          // data pipeline
          'startPhase',
          'endPhase',
          'bucketOutput',
        ].includes(key)
      )
    );
    // if task.attributes.samples is available, convert the array of objects to array of strings by taking the object's name property only
    if (Array.isArray(censoredAttributes.samples)) {
      censoredAttributes.samples = censoredAttributes.samples.map(
        (sample: { name: string }) => sample.name
      );
    }
  }
  detailsContent.value = censoredAttributes;
  showDetailsModal.value = true;
}

// --- Expose Properties to the Outside ---
defineExpose({
  tasks,
  pagingTotalRows,
  tasksQueryInProgress,
  requestSearch,
});

// --- Manage Cards Layout Changes ---
function onLayoutNeeded(phase: 'start' | 'end' | 'resize', id?: number | null) {
  console.debug('Layout requested, phase: ', phase, 'id:', id);

  // check task ID provided
  if (!id) {
    console.warn('Layout requested but no task ID provided');
    return;
  }

  // identify the container element for the task
  const container = document.querySelector(`#task-${id}`) as HTMLElement | null;

  // check container exists
  if (!container) {
    console.warn(`Layout requested but container #task-${id} not found`);
    return;
  }

  // pause transitions while heights are changing
  if (phase === 'start' && disableTransitionsState.value) {
    console.debug('Pausing transitions for layout');
    container.classList.add('task-no-anim');
  }

  // resume transitions when layout is complete
  if (phase === 'end' && disableTransitionsState.value) {
    requestAnimationFrame(() => {
      console.debug('Resuming transitions after layout');
      container.classList.remove('task-no-anim');
    });
  }
}

// --- Router Helpers for Navigation ---
function bucketRequested(
  bucket: BucketType | undefined | null,
  index?: number,
  evt?: MouseEvent,
  path?: string
) {
  if (bucket && bucket.id) {
    // redirect to bucket detail page if permitted
    if (!bucket.deletedAt) {
      openRouterLink(
        {
          name: 'BucketDetail',
          params: { id: bucket.id },
          query: {
            ...(path ? { path } : {}),
            showMetadata: '1',
            allowDownload: '1',
            listComputeTasks: '0',
            listSamples: '0',
            listProjects: '0',
            listUsers: '0',
            returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
            returnLabel: 'Project Detail',
          },
        },
        router,
        evt
      );
    } else {
      alert('The bucket for this task has been deleted.');
    }
  }
}
</script>
<template>
  <div>
    <!-- loading and error alerts -->
    <b-alert v-if="tasksQueryInProgress" :model-value="true" variant="info">
      Loading...
    </b-alert>
    <b-alert v-if="tasksQueryError" :model-value="true" variant="danger">
      {{ tasksQueryError.message }}
      <b-button
        @click="requestRefetchTasks"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- empty list -->
    <b-alert :model-value="noTasks && !tasksQueryError" variant="secondary">
      <span v-if="tasksQueryInProgress">
        Loading tasks <b-spinner small />
      </span>
      <span v-else> {{ props.messageNoItemsFound }} </span>
    </b-alert>

    <!-- List tasks -->
    <template v-for="(rootTask, idx) in rootTasks" :key="rootTask.id || idx">
      <TaskResultState
        v-if="getExpanded(rootTask.id)"
        :error="detailErrors[rootTask.id!]"
      />
      <NextflowTask
        :id="`task-${rootTask.id}`"
        :task="rootTask"
        class="mb-2"
        :expanded="getExpanded(rootTask.id)"
        :selected-tab="getSelectedTab(rootTask.id)"
        @update:expanded="(val) => setExpanded(rootTask.id ?? 0, val)"
        @update:selected-tab="val =>
      setSelectedTab(rootTask.id!, val)"
        @layout-needed="onLayoutNeeded"
        @bucket-requested="bucketRequested(rootTask.bucket, idx, $event)"
        @exec-details-requested="showExecDetails(rootTask)"
        @debug-details-requested="debugTaskId = rootTask.id || null"
        @abort-requested="taskAbortRequested(rootTask)"
        @directory-info-requested="
          (file) => directoryInfoRequested(rootTask, file)
        "
        @directory-view-requested="
          (file) => directoryViewRequested(rootTask, file)
        "
        @file-preview-requested="(file) => filePreviewRequested(rootTask, file)"
        @file-download-requested="
          (file) => fileDownloadRequested(rootTask, file)
        "
      />
    </template>

    <!-- Paging (below) -->
    <BPagination
      v-if="
        pagingItemsPerPage > 0 && pagingTotalRows > props.pagingItemsPerPage
      "
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      aria-label="Nextflow task pages"
      pills
      size="sm"
    />

    <ComputeTaskDebugDetails
      v-if="debugTaskId"
      :key="debugTaskId"
      :task-id="debugTaskId"
      :project-id="props.projectId"
      @close="debugTaskId = null"
    />

    <!-- Exec Details Modal -->
    <BModal
      v-model="showDetailsModal"
      :class="[detailsContent ? 'modal-xl' : 'modal-sm']"
      @close="
        showDetailsModal = false;
        detailsContent = null;
      "
    >
      <template #header>
        <h2>
          <font-awesome-icon icon="sliders" class="me-2" />
          {{ detailsModalTitle }}
        </h2>
      </template>
      <div>
        <pre
          v-if="detailsContent"
          class="code-highlight"
          v-html="highlightJson(detailsContent)"
        ></pre>
        <p v-else>
          <font-awesome-icon icon="exclamation-triangle" />
          <strong>No Details</strong>
        </p>
      </div>
      <!-- Modal footer with close button only -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="
            showDetailsModal = false;
            detailsContent = null;
          "
        >
          Close
        </b-button>
      </template>
    </BModal>

    <!-- MODAL: Data Object Detail -->
    <div
      v-if="
        selectedDataObject &&
        selectedTask?.bucket?.name &&
        selectedTask?.bucket?.id
      "
    >
      <DataObjectDetail
        :show="showModalDataObjectDetail"
        :data-object="selectedDataObject"
        :bucket-name="selectedTask.bucket.name"
        :bucket-id="selectedTask.bucket.id"
        :allow-download="selectedObjectDownloadAllowed"
        @close="
          showModalDataObjectDetail = false;
          selectedDataObject = null;
        "
      />
    </div>

    <!-- MODAL: Abort Compute Task -->
    <ComputeTaskAbort
      v-if="showConfirmAbortModal && selectedTask?.id"
      :show="showConfirmAbortModal"
      :task="selectedTask"
      @close="closeTaskAbortModal"
      @close-and-refresh="closeTaskAbortModal"
    />
  </div>
</template>
<style scoped>
.root-task-card {
  h2 {
    font-size: 1rem;
    font-weight: bold;
  }
}

/* grid layout with auto-fit and min column width */
.tasks-grid {
  /* Responsive columns: as many as fit, min width = --col-min */
  display: grid;
  gap: 12px; /* card gutter */
  align-items: start; /* keep tops aligned */
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(100%, var(--col-min, 450px)), 1fr)
  );
}

/* freeze transitions while many collapses toggle at once */
.task-no-anim :is(.collapse, .collapsing, .card, .card-body) {
  transition: none !important;
}
</style>
